import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import Table from '@mui/joy/Table';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import CircularProgress from '@mui/joy/CircularProgress';
import Chip from '@mui/joy/Chip';
import Dropdown from '@mui/joy/Dropdown';
import Menu from '@mui/joy/Menu';
import MenuButton from '@mui/joy/MenuButton';
import MenuItem from '@mui/joy/MenuItem';
import ListDivider from '@mui/joy/ListDivider';
import IconButton from '@mui/joy/IconButton';
import Input from '@mui/joy/Input';
import Link from '@mui/joy/Link';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Confirm from '../common/Confirm';
import {
  listDeployments,
  createDeployment,
  deleteDeploymentListener,
  getListenerRoomKey,
  getAgent,
} from '../../api/agent';
import useTelephonyCallerEndpoints from '../../hooks/useTelephonyCallerEndpoints';
import TelephonyCallerEndpointFields from '../agent/TelephonyCallerEndpointFields';
import LazyAgentSelect from '../agent/LazyAgentSelect';

function handlerFromModelName(modelName) {
  if (modelName == null || String(modelName).trim() === '') return null;
  const m = String(modelName).match(/^([a-z0-9-_]+):/i);
  return m ? m[1] : null;
}

/** Newest listener first; when `selectedAgentId` is set (all-deployments view), rows for that agent appear first. */
function sortDeploymentRows(rows, selectedAgentId) {
  const list = Array.isArray(rows) ? [...rows] : [];
  const ts = (r) => new Date(r.createdAt || 0).getTime();
  return list.sort((a, b) => {
    if (selectedAgentId) {
      const ac = a.agentId === selectedAgentId ? 0 : 1;
      const bc = b.agentId === selectedAgentId ? 0 : 1;
      if (ac !== bc) return ac - bc;
    }
    return ts(b) - ts(a);
  });
}

function formatListenerCreated(iso) {
  if (iso == null || String(iso).trim() === '') return '–';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '–';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Backend origin for `AplisayWidget` `url` (no `/api` suffix). Matches REACT_APP_BACKEND_SERVER. */
function widgetBackendOrigin() {
  const raw = process.env.REACT_APP_BACKEND_SERVER || '';
  try {
    const u = new URL(raw);
    let path = u.pathname.replace(/\/api\/?$/, '');
    if (path !== '/' && path.endsWith('/')) path = path.slice(0, -1);
    return `${u.origin}${path === '/' ? '' : path}`;
  } catch {
    return String(raw)
      .replace(/\/api\/?$/, '')
      .replace(/\/$/, '');
  }
}

function DeploymentTable({
  rows,
  onDelete,
  onShowWidgetCredentials,
  onAgentNameClick,
  agentNameActionPending,
  emptyMessage,
}) {
  if (!rows?.length) {
    return (
      <Sheet variant="soft" sx={{ p: 2, borderRadius: 'sm' }}>
        <Typography level="body-sm">{emptyMessage}</Typography>
      </Sheet>
    );
  }
  const cellClampSx = {
    maxWidth: 0,
    overflow: 'hidden',
    verticalAlign: 'middle',
  };

  return (
    <Sheet
      variant="outlined"
      sx={{
        borderRadius: 'sm',
        width: '100%',
        maxWidth: '100%',
        overflow: 'auto',
      }}
    >
      {/* Mobile table: Agent / Mode / actions */}
      <Table
        stickyHeader
        hoverRow
        sx={{
          display: { xs: 'table', sm: 'none' },
          tableLayout: 'fixed',
          width: '100%',
        }}
      >
        <colgroup>
          <col style={{ width: '62%' }} />
          <col style={{ width: '26%' }} />
          <col style={{ width: '12%' }} />
        </colgroup>
        <thead>
          <tr>
            <th>Agent</th>
            <th>Mode</th>
            <th aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.listenerId}>
              <Box component="td" sx={cellClampSx}>
                {typeof onAgentNameClick === 'function' ? (
                  <Box
                    component="button"
                    type="button"
                    title="Save the open agent (if changed), then open this agent in Build"
                    disabled={agentNameActionPending}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onAgentNameClick(row);
                    }}
                    sx={{
                      typography: 'body-sm',
                      display: 'block',
                      width: '100%',
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      p: 0,
                      m: 0,
                      border: 'none',
                      background: 'none',
                      fontWeight: 'md',
                      textAlign: 'inherit',
                      cursor: agentNameActionPending ? 'wait' : 'pointer',
                      color: 'primary.plainColor',
                      '&:hover:not(:disabled)': { textDecoration: 'underline' },
                      '&:disabled': { opacity: 0.65 },
                    }}
                  >
                    {row.agentName || '–'}
                  </Box>
                ) : (
                  <Typography
                    level="body-sm"
                    fontWeight="md"
                    title={row.agentName || undefined}
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.agentName || '–'}
                  </Typography>
                )}
              </Box>
              <Box component="td" sx={{ verticalAlign: 'middle' }}>
                <Chip
                  size="sm"
                  variant="soft"
                  color={
                    row.kind === 'phone'
                      ? 'primary'
                      : row.kind === 'registration'
                      ? 'warning'
                      : 'success'
                  }
                >
                  {row.kind === 'phone'
                    ? 'Phone'
                    : row.kind === 'registration'
                    ? 'Reg'
                    : 'WebRTC'}
                </Chip>
              </Box>
              <Box component="td" sx={{ ...cellClampSx, verticalAlign: 'middle', textAlign: 'right' }}>
                <Dropdown>
                  <MenuButton
                    slots={{ root: IconButton }}
                    size="sm"
                    variant="plain"
                    color="neutral"
                    aria-label="Deployment actions"
                    sx={{ flex: '0 0 auto', mr: -0.5 }}
                  >
                    <MoreVertIcon />
                  </MenuButton>
                  <Menu size="sm" placement="left-start" variant="soft">
                    {row.kind === 'webrtc' && typeof onShowWidgetCredentials === 'function' && (
                      <>
                        <MenuItem onClick={() => onShowWidgetCredentials(row)} startDecorator={<VpnKeyIcon />}>
                          View credentials
                        </MenuItem>
                        <ListDivider />
                      </>
                    )}
                    <MenuItem
                      color="danger"
                      onClick={() => onDelete(row)}
                      startDecorator={<DeleteOutlineIcon />}
                    >
                      Remove deployment
                    </MenuItem>
                  </Menu>
                </Dropdown>
              </Box>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Desktop table: full detail */}
      <Table
        stickyHeader
        hoverRow
        sx={{
          display: { xs: 'none', sm: 'table' },
          tableLayout: 'fixed',
          width: '100%',
        }}
      >
        <colgroup>
          <col style={{ width: '22%' }} />
          <col style={{ width: '14%' }} />
          <col style={{ width: '24%' }} />
          <col style={{ width: '14%' }} />
          <col style={{ width: '26%' }} />
        </colgroup>
        <thead>
          <tr>
            <th>Agent</th>
            <th>Mode</th>
            <th>Endpoint</th>
            <th>Created</th>
            <th>Backend</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.listenerId}>
              <Box component="td" sx={cellClampSx}>
                {typeof onAgentNameClick === 'function' ? (
                  <Box
                    component="button"
                    type="button"
                    title="Save the open agent (if changed), then open this agent in Build"
                    disabled={agentNameActionPending}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onAgentNameClick(row);
                    }}
                    sx={{
                      typography: 'body-sm',
                      display: 'block',
                      width: '100%',
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      p: 0,
                      m: 0,
                      border: 'none',
                      background: 'none',
                      fontWeight: 'md',
                      textAlign: 'inherit',
                      cursor: agentNameActionPending ? 'wait' : 'pointer',
                      color: 'primary.plainColor',
                      '&:hover:not(:disabled)': { textDecoration: 'underline' },
                      '&:disabled': { opacity: 0.65 },
                    }}
                  >
                    {row.agentName || '–'}
                  </Box>
                ) : (
                  <Typography
                    level="body-sm"
                    fontWeight="md"
                    title={row.agentName || undefined}
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.agentName || '–'}
                  </Typography>
                )}
              </Box>
              <Box component="td" sx={{ verticalAlign: 'middle' }}>
                <Chip
                  size="sm"
                  variant="soft"
                  color={
                    row.kind === 'phone'
                      ? 'primary'
                      : row.kind === 'registration'
                      ? 'warning'
                      : 'success'
                  }
                >
                  {row.kind === 'phone'
                    ? 'Phone Number'
                    : row.kind === 'registration'
                    ? 'Phone Registration'
                    : 'WebRTC'}
                </Chip>
              </Box>
              <Box component="td" sx={cellClampSx}>
                <Typography
                  level="body-sm"
                  title={
                    row.kind === 'phone'
                      ? row.phoneNumber || undefined
                      : row.kind === 'registration'
                      ? row.registrationName || 'Registration endpoint'
                      : 'WebRTC listener (join via API / UI)'
                  }
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {row.kind === 'phone'
                    ? row.phoneNumber || '–'
                    : row.kind === 'registration'
                    ? row.registrationName || 'Registration endpoint'
                    : 'WebRTC listener (join via API / UI)'}
                </Typography>
              </Box>
              <Box component="td" sx={cellClampSx}>
                <Typography
                  level="body-xs"
                  textColor="neutral.600"
                  title={row.createdAt ? new Date(row.createdAt).toISOString() : undefined}
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatListenerCreated(row.createdAt)}
                </Typography>
              </Box>
              <Box component="td" sx={{ ...cellClampSx, verticalAlign: 'middle' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5, minWidth: 0 }}>
                  <Typography
                    level="body-xs"
                    textColor="neutral.600"
                    title={row.type || undefined}
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      minWidth: 0,
                      flex: 1,
                    }}
                  >
                    {row.type || '–'}
                  </Typography>
                  <Dropdown>
                    <MenuButton
                      slots={{ root: IconButton }}
                      size="sm"
                      variant="plain"
                      color="neutral"
                      aria-label="Deployment actions"
                      sx={{ flex: '0 0 auto', mr: -0.5 }}
                    >
                      <MoreVertIcon />
                    </MenuButton>
                    <Menu size="sm" placement="left-start" variant="soft">
                      {row.kind === 'webrtc' && typeof onShowWidgetCredentials === 'function' && (
                        <>
                          <MenuItem onClick={() => onShowWidgetCredentials(row)} startDecorator={<VpnKeyIcon />}>
                            View credentials
                          </MenuItem>
                          <ListDivider />
                        </>
                      )}
                      <MenuItem color="danger" onClick={() => onDelete(row)} startDecorator={<DeleteOutlineIcon />}>
                        Remove deployment
                      </MenuItem>
                    </Menu>
                  </Dropdown>
                </Box>
              </Box>
            </tr>
          ))}
        </tbody>
      </Table>
    </Sheet>
  );
}

export default function DeploymentsPanel({ setError, setInform, selectedAgentId }) {
  const [currentItems, setCurrentItems] = useState([]);
  const [loadingCurrent, setLoadingCurrent] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [allItems, setAllItems] = useState([]);
  const [loadingAll, setLoadingAll] = useState(false);
  const [currentAgentLabel, setCurrentAgentLabel] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Only ever holds agents we've actually resolved (the pinned current agent and
  // any the user picks) — carries `modelName` so `selectedAgent`/`handlerFilter`
  // below can filter telephony endpoints. The dropdown itself fetches its options
  // lazily via <LazyAgentSelect>, so this never enumerates every agent.
  const [agents, setAgents] = useState([]);
  const [agentId, setAgentId] = useState('');
  const [kind, setKind] = useState('webrtc');
  const [selectedPhone, setSelectedPhone] = useState('');
  const [selectedRegistrationId, setSelectedRegistrationId] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const [credentialsOpen, setCredentialsOpen] = useState(false);
  const [credentialsLoading, setCredentialsLoading] = useState(false);
  const [credentialsError, setCredentialsError] = useState(null);
  const [credentialsData, setCredentialsData] = useState(null);

  const selectedAgent = useMemo(() => agents.find((a) => a.id === agentId), [agents, agentId]);
  const handlerFilter = useMemo(() => handlerFromModelName(selectedAgent?.modelName), [selectedAgent]);

  // Pin the currently selected agent at the top of the picker and keep its label
  // even before its full record resolves.
  const pickerCurrentAgent = useMemo(
    () => (agentId ? { id: agentId, name: selectedAgent?.name } : undefined),
    [agentId, selectedAgent],
  );

  // A pick only gives us {id,name}; fetch the full record to learn its modelName
  // (needed by handlerFilter). Keep just {id,name,modelName} — the bare minimum.
  const handlePickAgent = useCallback(async (id) => {
    setAgentId(id || '');
    if (!id) return;
    try {
      const a = await getAgent(id);
      setAgents((prev) => [
        { id: a.id, name: a.name, modelName: a.modelName },
        ...prev.filter((p) => p.id !== id),
      ]);
    } catch {
      /* handler filter falls back to unfiltered; selection still works */
    }
  }, []);

  const { phoneNumbers, registrations, loading: loadingEndpoints, error: endpointsFetchError } =
    useTelephonyCallerEndpoints(
      handlerFilter,
      'deploy',
      createOpen && !!agentId && kind !== 'webrtc',
    );

  useEffect(() => {
    if (createOpen && endpointsFetchError) {
      setError?.(`Could not load phone / registration endpoints: ${endpointsFetchError}`);
    }
  }, [createOpen, endpointsFetchError, setError]);

  useEffect(() => {
    if (!selectedAgentId) {
      setCurrentAgentLabel('');
      return;
    }
    let cancelled = false;
    getAgent(selectedAgentId)
      .then((a) => {
        if (!cancelled) setCurrentAgentLabel((a?.name || 'Unnamed').trim() || selectedAgentId);
      })
      .catch(() => {
        if (!cancelled) setCurrentAgentLabel(selectedAgentId);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedAgentId]);

  const refreshCurrent = useCallback(async () => {
    if (!selectedAgentId) {
      setCurrentItems([]);
      setLoadingCurrent(false);
      return;
    }
    setLoadingCurrent(true);
    try {
      const data = await listDeployments({ agentId: selectedAgentId });
      const raw = Array.isArray(data?.items) ? data.items : [];
      setCurrentItems(sortDeploymentRows(raw, selectedAgentId));
    } catch (err) {
      setError?.(`Could not load deployments: ${err?.response?.data?.message || err?.message || err}`);
      setCurrentItems([]);
    } finally {
      setLoadingCurrent(false);
    }
  }, [selectedAgentId, setError]);

  const refreshAll = useCallback(async () => {
    setLoadingAll(true);
    try {
      const data = await listDeployments();
      const rows = Array.isArray(data?.items) ? data.items : [];
      setAllItems(sortDeploymentRows(rows, selectedAgentId));
    } catch (err) {
      setError?.(`Could not load all deployments: ${err?.response?.data?.message || err?.message || err}`);
      setAllItems([]);
    } finally {
      setLoadingAll(false);
    }
  }, [selectedAgentId, setError]);

  useEffect(() => {
    setShowAll(false);
    setAllItems([]);
    refreshCurrent();
  }, [refreshCurrent, selectedAgentId]);

  const handleShowAll = async () => {
    setShowAll(true);
    await refreshAll();
  };

  const refreshAfterMutation = async () => {
    await refreshCurrent();
    if (showAll) await refreshAll();
  };

  const openCreate = async () => {
    setCreateOpen(true);
    setKind('webrtc');
    setSelectedPhone('');
    setSelectedRegistrationId('');
    setAgentId(selectedAgentId || '');
    // Resolve only the current agent up front — for its modelName (handler
    // filter) and pinned label. The dropdown lazily fetches its options on open.
    if (selectedAgentId) {
      try {
        const a = await getAgent(selectedAgentId);
        setAgents([{ id: a.id, name: a.name, modelName: a.modelName }]);
      } catch {
        setAgents([{ id: selectedAgentId }]);
      }
    } else {
      setAgents([]);
    }
  };

  const handleCreate = async () => {
    if (!agentId) {
      setError?.('Select an agent.');
      return;
    }
    setSubmitting(true);
    try {
      if (kind === 'webrtc') {
        await createDeployment(agentId, 'webrtc');
      } else if (kind === 'phone') {
        if (!selectedPhone) {
          setError?.('Select an allocated number.');
          setSubmitting(false);
          return;
        }
        await createDeployment(agentId, 'phone', selectedPhone);
      } else {
        if (!selectedRegistrationId) {
          setError?.('Select a registration endpoint.');
          setSubmitting(false);
          return;
        }
        await createDeployment(agentId, 'registration', selectedRegistrationId);
      }
      setInform?.({ success: 'Deployment created.' });
      setCreateOpen(false);
      await refreshAfterMutation();
    } catch (err) {
      setError?.(`Create failed: ${err?.response?.data?.message || err?.message || err}`);
    } finally {
      setSubmitting(false);
    }
  };

  const requestDelete = (row) => {
    setPendingDelete(row);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = () => {
    const listenerId = pendingDelete?.listenerId;
    if (!listenerId) return;
    (async () => {
      try {
        await deleteDeploymentListener(listenerId);
        setInform?.({ success: 'Deployment removed.' });
        setPendingDelete(null);
        await refreshAfterMutation();
      } catch (err) {
        setError?.(`Delete failed: ${err?.response?.data?.message || err?.message || err}`);
      }
    })();
  };

  const openWidgetCredentials = useCallback(async (row) => {
    setCredentialsOpen(true);
    setCredentialsLoading(true);
    setCredentialsError(null);
    setCredentialsData(null);
    try {
      const data = await getListenerRoomKey(row.listenerId);
      setCredentialsData(data);
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        String(err);
      setCredentialsError(msg);
    } finally {
      setCredentialsLoading(false);
    }
  }, []);

  const copyCredential = useCallback(
    (text) => {
      if (text == null || text === '') return;
      navigator.clipboard.writeText(String(text)).then(
        () => setInform?.({ success: 'Copied to clipboard.' }),
        () => setError?.('Could not copy to clipboard.'),
      );
    },
    [setInform, setError],
  );

  const widgetSnippet = useMemo(() => {
    const url = widgetBackendOrigin();
    const lid = credentialsData?.listenerId ?? '';
    const rk = credentialsData?.roomKey ?? '';
    return `import AplisayWidget from '@aplisay/react-widget';
import '@aplisay/react-widget/dist/styles.css';

<AplisayWidget
  url="${url}"
  listenerId="${lid}"
  roomKey="${rk}"
/>`;
  }, [credentialsData]);

  return (
    <Box sx={{ py: 2, width: '100%', minWidth: 0, boxSizing: 'border-box' }}>
      <Sheet
        variant="outlined"
        sx={{
          p: { xs: 1.25, sm: 0 },
          mb: { xs: 1.5, sm: 0 },
          mx: { xs: 1, sm: 0 },
          borderRadius: { xs: 'sm', sm: 0 },
          border: { xs: '1px solid', sm: 'none' },
          borderColor: { xs: 'divider', sm: 'transparent' },
          bgcolor: { xs: 'background.surface', sm: 'transparent' },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: { xs: 1, sm: 2 } }}>
          <Typography level="h4" sx={{ display: { xs: 'none', sm: 'block' } }}>
            Deploy
          </Typography>
          <Button startDecorator={<AddIcon />} onClick={openCreate} size="sm">
            Create deployment
          </Button>
        </Box>

        <Typography level="body-sm" sx={{ mb: 2, maxWidth: 720 }}>
          Long-running listeners for your agents: phone numbers or WebRTC room endpoints. Use the Build tab to edit agent
          definitions.
        </Typography>

        <Typography level="title-sm" sx={{ mb: 0.5 }}>
          Current agent
        </Typography>
        <Typography level="body-sm" sx={{ mb: 0 }} textColor={selectedAgentId ? 'neutral.700' : 'neutral.500'}>
          {selectedAgentId
            ? currentAgentLabel || '…'
            : 'No agent selected — use the Agent menu to load an agent.'}
        </Typography>
      </Sheet>

      {loadingCurrent ? (
        <Sheet variant="soft" sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Sheet>
      ) : (
        <DeploymentTable
          rows={currentItems}
          onDelete={requestDelete}
          onShowWidgetCredentials={openWidgetCredentials}
          emptyMessage={
            selectedAgentId
              ? 'No deployments for this agent yet. Create one below.'
              : 'Select an agent to see its deployments here.'
          }
        />
      )}

      {!showAll && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <Button variant="outlined" color="neutral" onClick={handleShowAll} disabled={loadingAll}>
            Show all deployments
          </Button>
        </Box>
      )}

      {showAll && (
        <Box sx={{ mt: 3 }}>
          <Typography level="title-sm" sx={{ mb: 1 }}>
            All deployments
          </Typography>
          {loadingAll ? (
            <Sheet variant="soft" sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Sheet>
          ) : (
            <DeploymentTable
              rows={allItems}
              onDelete={requestDelete}
              onShowWidgetCredentials={openWidgetCredentials}
              emptyMessage="No deployments for any agent."
            />
          )}
        </Box>
      )}

      <Confirm
        open={deleteConfirmOpen}
        setOpen={(open) => {
          setDeleteConfirmOpen(open);
          if (!open) setPendingDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title={
          pendingDelete?.kind === 'phone'
            ? 'Remove phone deployment?'
            : pendingDelete?.kind === 'registration'
            ? 'Remove registration deployment?'
            : 'Remove WebRTC deployment?'
        }
      >
            {pendingDelete && (
              <>
                {pendingDelete.kind === 'phone' && (
                  <Typography level="body-sm">
                    This stops inbound call routing to{' '}
                    <Typography component="span" fontWeight="lg">
                      {pendingDelete.agentName || 'this agent'}
                    </Typography>
                    {pendingDelete.phoneNumber ? ` for ${pendingDelete.phoneNumber}` : ''}. Callers using this
                    deployment will no longer reach the agent.
                  </Typography>
                )}
                {pendingDelete.kind === 'registration' && (
                  <Typography level="body-sm">
                    This unlinks the registration endpoint from{' '}
                    <Typography component="span" fontWeight="lg">
                      {pendingDelete.agentName || 'this agent'}
                    </Typography>
                    . Calls arriving via this SIP registration will no longer reach the agent.
                  </Typography>
                )}
                {pendingDelete.kind === 'webrtc' && (
                  <Typography level="body-sm">
                    This removes the WebRTC listener for{' '}
                    <Typography component="span" fontWeight="lg">
                      {pendingDelete.agentName || 'this agent'}
                    </Typography>
                    . Playground joins, API room access, and the Aplisay embed widget using this listener will
                    no longer connect.
                  </Typography>
                )}
            <Typography level="body-xs" sx={{ mt: 1.5 }} textColor="neutral.600">
              Backend: {pendingDelete.type || '–'} ·{' '}
              {pendingDelete.kind === 'phone'
                ? 'Phone'
                : pendingDelete.kind === 'registration'
                ? 'Registration'
                : 'WebRTC'}
            </Typography>
            <Typography
              level="body-xs"
              sx={{ mt: 1, fontFamily: 'ui-monospace, monospace', wordBreak: 'break-all' }}
              textColor="neutral.500"
            >
              {pendingDelete.listenerId}
            </Typography>
          </>
        )}
      </Confirm>

      <Modal
        open={credentialsOpen}
        onClose={() => {
          setCredentialsOpen(false);
          setCredentialsError(null);
          setCredentialsData(null);
        }}
      >
        <ModalDialog sx={{ minWidth: { xs: '100%', sm: 480 }, maxWidth: 560 }}>
          <ModalClose />
          <Typography level="h4" sx={{ mb: 1 }}>
            Aplisay widget credentials
          </Typography>
          <Typography level="body-sm" sx={{ mb: 2 }}>
            Use with the configurator at{' '}
            <Link href="https://widget.aplisay.com" target="_blank" rel="noreferrer">
              widget.aplisay.com
            </Link>{' '}
            or <Typography component="span" fontFamily="code">@aplisay/react-widget</Typography> (
            <code>url</code>, <code>listenerId</code>, <code>roomKey</code>).
          </Typography>
          {credentialsLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size="sm" />
            </Box>
          )}
          {credentialsError && (
            <Typography level="body-sm" sx={{ mb: 2, color: 'danger.500' }}>
              {credentialsError}
            </Typography>
          )}
          {!credentialsLoading && credentialsData && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {[
                { label: 'url', value: widgetBackendOrigin() },
                { label: 'listenerId', value: credentialsData.listenerId },
                { label: 'roomKey', value: credentialsData.roomKey },
              ].map(({ label, value }) => (
                <FormControl key={label} size="sm">
                  <FormLabel>{label}</FormLabel>
                  <Input
                    readOnly
                    value={value ?? ''}
                    slotProps={{ input: { sx: { fontFamily: 'ui-monospace, monospace', fontSize: '0.85em' } } }}
                    endDecorator={
                      <IconButton
                        size="sm"
                        variant="plain"
                        aria-label={`Copy ${label}`}
                        onClick={() => copyCredential(value)}
                      >
                        <ContentCopyIcon />
                      </IconButton>
                    }
                  />
                </FormControl>
              ))}
              <FormControl size="sm">
                <FormLabel>Example</FormLabel>
                <Sheet variant="outlined" sx={{ p: 1.5, borderRadius: 'sm', overflow: 'auto' }}>
                  <Typography
                    component="pre"
                    sx={{
                      m: 0,
                      fontFamily: 'ui-monospace, monospace',
                      fontSize: '0.75rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {widgetSnippet}
                  </Typography>
                </Sheet>
                <Button
                  size="sm"
                  variant="outlined"
                  sx={{ mt: 1, alignSelf: 'flex-start' }}
                  onClick={() => copyCredential(widgetSnippet)}
                >
                  Copy example
                </Button>
              </FormControl>
            </Box>
          )}
        </ModalDialog>
      </Modal>

      <Modal open={createOpen} onClose={() => !submitting && setCreateOpen(false)}>
        <ModalDialog sx={{ minWidth: { xs: '100%', sm: 440 } }}>
          <ModalClose />
          <Typography level="h4" sx={{ mb: 2 }}>
            Create deployment
          </Typography>
          <Typography level="body-sm" sx={{ mb: 2 }}>
            Starts a listener for an existing agent. WebRTC exposes a room you can join from the playground; phone routes
            inbound calls to the agent.
          </Typography>

          <FormControl sx={{ mb: 2 }}>
            <FormLabel>Agent</FormLabel>
            <LazyAgentSelect
              value={agentId || null}
              onChange={handlePickAgent}
              currentAgent={pickerCurrentAgent}
              placeholder="Choose agent…"
              searchable
              sx={{ minWidth: 280 }}
            />
          </FormControl>

          <FormControl sx={{ mb: 2 }}>
            <FormLabel>Listener type</FormLabel>
            <Select
              value={kind}
              onChange={(_, v) => {
                const next = v ?? 'webrtc';
                setKind(next);
                if (next === 'webrtc') {
                  setSelectedPhone('');
                  setSelectedRegistrationId('');
                } else if (next === 'phone') {
                  setSelectedRegistrationId('');
                } else if (next === 'registration') {
                  setSelectedPhone('');
                }
              }}
              sx={{ minWidth: 280 }}
            >
              <Option value="webrtc">WebRTC (browser / room join)</Option>
              <Option value="phone">Phone number (inbound SIP / PSTN)</Option>
              <Option value="registration">Registration (inbound via SIP registration)</Option>
            </Select>
          </FormControl>

          {(kind === 'phone' || kind === 'registration') && (
            <TelephonyCallerEndpointFields
              purpose="deploy"
              callerKind={kind === 'phone' ? 'phone' : 'registration'}
              kind={kind}
              onKindChange={() => {}}
              phoneNumbers={phoneNumbers}
              registrations={registrations}
              loading={loadingEndpoints}
              selectedPhone={selectedPhone}
              onSelectedPhone={setSelectedPhone}
              selectedRegistrationId={selectedRegistrationId}
              onSelectedRegistrationId={setSelectedRegistrationId}
              emptyPhoneHint={
                "No allocated numbers available for this agent's backend. Add numbers under Numbering first."
              }
              emptyRegistrationHint={
                "No registration endpoints available for this agent's backend. Configure registrations under Registrations first."
              }
            />
          )}

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
            <Button variant="plain" color="neutral" onClick={() => setCreateOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={handleCreate} loading={submitting}>
              Create
            </Button>
          </Box>
        </ModalDialog>
      </Modal>
    </Box>
  );
}
